import { detectDynamicVariables } from '@/utils/templateValidation';
import { z } from 'zod';

// Supported languages (82 languages as per WhatsApp requirements)
const SUPPORTED_LANGUAGES = [
  'af',
  'sq',
  'ar',
  'ar_EG',
  'ar_AE',
  'ar_LB',
  'ar_MA',
  'ar_QA',
  'az',
  'be_BY',
  'bn',
  'bg',
  'ca',
  'zh_CN',
  'zh_HK',
  'zh_TW',
  'hr',
  'cs',
  'da',
  'nl',
  'en',
  'en_GB',
  'en_US',
  'et',
  'fil',
  'fi',
  'fr',
  'ka',
  'de',
  'el',
  'gu',
  'ha',
  'he',
  'hi',
  'hu',
  'id',
  'ga',
  'it',
  'ja',
  'kn',
  'kk',
  'rw_RW',
  'ko',
  'ky_KG',
  'lo',
  'lv',
  'lt',
  'mk',
  'ms',
  'ml',
  'mr',
  'nb',
  'fa',
  'pl',
  'pt_BR',
  'pt_PT',
  'pa',
  'ro',
  'ru',
  'sr',
  'sk',
  'sl',
  'es',
  'es_AR',
  'es_ES',
  'es_MX',
  'sw',
  'sv',
  'ta',
  'te',
  'th',
  'tr',
  'uk',
  'ur',
  'uz',
  'vi',
  'zu',
];

// Template categories
const TEMPLATE_CATEGORIES = ['authentication', 'marketing', 'utility'];

// Header formats
const HEADER_FORMATS = ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];

// Button types
const BUTTON_TYPES = ['copy_code', 'url', 'phone_number', 'quick_reply'];

// Validation limits
const VALIDATION_LIMITS = {
  MAX_TEMPLATE_NAME: 512,
  MAX_BODY_TEXT: 1024,
  MAX_HEADER_TEXT: 60,
  MAX_FOOTER_TEXT: 60,
  MAX_LIMITED_TIME_OFFER: 16,
  MAX_BUTTON_TEXT: 20,
  MAX_BUTTON_EXAMPLE: 15,
  MAX_BUTTON_URL_EXAMPLE: 2000,
  MAX_BUTTONS: 10,
  EDIT_TIME_LIMIT_HOURS: 24,
};

// Helper function to validate template name
const validateTemplateName = name => {
  const errors = [];

  // Check alphanumeric + underscore only
  if (!/^[a-zA-Z0-9_]+$/.test(name)) {
    errors.push(
      'Template name can only contain alphanumeric characters and underscores'
    );
  }

  return errors;
};

// Helper function to validate phone number format
const validatePhoneNumber = phone => {
  // International format validation (basic)
  const phoneRegex = /^\+[1-9]\d{1,14}$/;
  return phoneRegex.test(phone);
};

// Helper function to validate URL format
const validateUrl = url => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Header schema
const HeaderSchema = z
  .object({
    format: z.preprocess(
      val => (val === '' || val === undefined ? undefined : val),
      z.enum(HEADER_FORMATS).optional()
    ),
    text: z.string().max(VALIDATION_LIMITS.MAX_HEADER_TEXT).optional(),
    url: z.string().optional().or(z.literal('')),
    key: z.string().optional().or(z.literal('')),
    header_text: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.format || data.format.trim() === '') return;

    if (data.format === 'TEXT' && !data.text) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['text'],
        message: 'Header text is required for TEXT format',
      });
    }

    if (data.format === 'TEXT' && data.text) {
      if (/[\r\n]/.test(data.text)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['text'],
          message: 'Header text cannot contain new lines',
        });
      }

      if (/[*]/.test(data.text)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['text'],
          message: 'Header text cannot contain asterisks (*)',
        });
      }

      if (/\p{Extended_Pictographic}|\p{Emoji_Presentation}/u.test(data.text)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['text'],
          message: 'Header text cannot contain emojis',
        });
      }
    }

    if (
      ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(data.format) &&
      !(data.url || data.header_text || data.key)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['attachment', 'file'],
        message: 'Media file is required',
      });
    }

    if (data.text) {
      const variables = detectDynamicVariables(data.text);
      if (variables.length > 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['text'],
          message: 'Header text can have maximum 1 dynamic variable ({{1}})',
        });
      }
    }

    if (data.header_text && data.header_text.length > 0) {
      let hasVars = false;
      if (data.text) {
        hasVars = detectDynamicVariables(data.text).length > 0;
      }
      if (!hasVars) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['header_text', 0],
          message:
            'Header text examples only allowed when dynamic variables are present',
        });
      }
    }
  });

// Body schema
const BodySchema = z
  .object({
    text: z.string().max(VALIDATION_LIMITS.MAX_BODY_TEXT),
    body_text: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.body_text && data.body_text.length > 0) {
      const variables = detectDynamicVariables(data.text);
      if (variables.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['body_text'],
          message:
            'Body text examples only allowed when dynamic variables are present',
        });
      }
    }
  });

// Footer schema
const FooterSchema = z.object({
  text: z.string().max(VALIDATION_LIMITS.MAX_FOOTER_TEXT),
});

// Limited time offer schema
const LimitedTimeOfferSchema = z.object({
  text: z.string().max(VALIDATION_LIMITS.MAX_LIMITED_TIME_OFFER),
  has_expiration: z.boolean().optional(),
});

// Button schema
const ButtonSchema = z
  .object({
    type: z.enum(BUTTON_TYPES),
    text: z.string().max(VALIDATION_LIMITS.MAX_BUTTON_TEXT).optional(),
    url: z.string().optional(),
    phoneNumber: z.string().optional(),
    example: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      ['url', 'phone_number', 'quick_reply'].includes(data.type) &&
      !data.text
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['text'],
        message: 'Button text is required',
      });
    }

    if (data.type === 'url' && !data.url) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['url'],
        message: 'URL is required',
      });
    }

    if (data.type === 'url' && data.url && !validateUrl(data.url)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['url'],
        message: 'Invalid URL format',
      });
    }

    if (data.type === 'phone_number' && !data.phoneNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['phoneNumber'],
        message: 'Phone number is required',
      });
    }

    if (
      data.type === 'phone_number' &&
      data.phoneNumber &&
      !validatePhoneNumber(data.phoneNumber)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['phoneNumber'],
        message: 'Invalid phone number format. Use international format.',
      });
    }

    if (data.type === 'copy_code') {
      if (!data.example) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['example'],
          message: 'Copy code button requires example',
        });
      } else if (data.example.length > VALIDATION_LIMITS.MAX_BUTTON_EXAMPLE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['example'],
          message: `Copy code example max ${VALIDATION_LIMITS.MAX_BUTTON_EXAMPLE} characters`,
        });
      }
    }

    if (data.type === 'url' && data.url) {
      const variables = detectDynamicVariables(data.url);
      if (variables.length > 0 && !data.example) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['example'],
          message:
            'URL button with dynamic variables requires example (max 2000 characters)',
        });
      }
      if (
        data.example &&
        data.example.length > VALIDATION_LIMITS.MAX_BUTTON_URL_EXAMPLE
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['example'],
          message: `URL example max ${VALIDATION_LIMITS.MAX_BUTTON_URL_EXAMPLE} characters`,
        });
      }
    }
  });

// Main template creation schema
export const CreateTemplateSchema = z
  .object({
    projectId: z.string().min(1, 'Project ID is required'),
    name: z
      .string()
      .min(1, 'Template name is required')
      .max(
        VALIDATION_LIMITS.MAX_TEMPLATE_NAME,
        `Template name must be less than ${VALIDATION_LIMITS.MAX_TEMPLATE_NAME} characters`
      )
      .transform(name => name.toLowerCase()) // Convert to lowercase
      .superRefine((name, ctx) => {
        const errors = validateTemplateName(name);
        if (errors.length > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: errors[0],
          });
        }
      }),
    category: z.enum(TEMPLATE_CATEGORIES),
    language: z.enum(SUPPORTED_LANGUAGES),
    body: BodySchema,
    header: HeaderSchema.optional(),
    footer: FooterSchema.optional(),
    limited_time_offer: LimitedTimeOfferSchema.optional(),
    buttons: z
      .array(ButtonSchema)
      .max(
        VALIDATION_LIMITS.MAX_BUTTONS,
        `Maximum ${VALIDATION_LIMITS.MAX_BUTTONS} buttons allowed`
      )
      .optional(),
  })
  .refine(
    data => {
      // Authentication templates cannot have header, footer, limited_time_offer, or buttons
      if (['AUTHENTICATION', 'authentication'].includes(data.category)) {
        if (
          data.header ||
          data.footer ||
          data.limited_time_offer ||
          data.buttons
        ) {
          return false;
        }
      }
      return true;
    },
    {
      message:
        'Authentication templates cannot have header, footer, limited time offer, or buttons',
      path: ['category'],
    }
  )
  .refine(
    data => {
      // Footer and limited_time_offer are mutually exclusive
      if (data.footer && data.limited_time_offer) {
        return false;
      }
      return true;
    },
    {
      message: 'Footer and limited time offer are mutually exclusive',
      path: ['limited_time_offer'],
    }
  )
  .superRefine((data, ctx) => {
    if (
      data.category &&
      !['AUTHENTICATION', 'authentication'].includes(data.category)
    ) {
      if (!data.body?.text || data.body.text.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['body', 'text'],
          message: 'Body text is required',
        });
      }
    }
  });

// Template update schema (all fields optional except projectId)
export const UpdateTemplateSchema = z
  .object({
    projectId: z.string().min(1, 'Project ID is required'),
    name: z
      .string()
      .min(1, 'Template name is required')
      .max(
        VALIDATION_LIMITS.MAX_TEMPLATE_NAME,
        `Template name must be less than ${VALIDATION_LIMITS.MAX_TEMPLATE_NAME} characters`
      )
      .superRefine((name, ctx) => {
        const errors = validateTemplateName(name);
        if (errors.length > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: errors[0],
          });
        }
      })
      .optional(),
    category: z.enum(TEMPLATE_CATEGORIES).optional(),
    language: z.enum(SUPPORTED_LANGUAGES).optional(),
    body: BodySchema.optional(),
    header: HeaderSchema.optional(),
    footer: FooterSchema.optional(),
    limited_time_offer: LimitedTimeOfferSchema.optional(),
    buttons: z
      .array(ButtonSchema)
      .max(
        VALIDATION_LIMITS.MAX_BUTTONS,
        `Maximum ${VALIDATION_LIMITS.MAX_BUTTONS} buttons allowed`
      )
      .optional(),
  })
  .refine(
    data => {
      // Authentication templates cannot have header, footer, limited_time_offer, or buttons
      if (
        data.category &&
        ['AUTHENTICATION', 'authentication'].includes(data.category)
      ) {
        if (
          data.header ||
          data.footer ||
          data.limited_time_offer ||
          data.buttons
        ) {
          return false;
        }
      }
      return true;
    },
    {
      message:
        'Authentication templates cannot have header, footer, limited time offer, or buttons',
      path: ['category'],
    }
  )
  .refine(
    data => {
      // Footer and limited_time_offer are mutually exclusive
      if (data.footer && data.limited_time_offer) {
        return false;
      }
      return true;
    },
    {
      message: 'Footer and limited time offer are mutually exclusive',
      path: ['limited_time_offer'],
    }
  )
  .superRefine((data, ctx) => {
    if (
      data.category &&
      !['AUTHENTICATION', 'authentication'].includes(data.category)
    ) {
      if (!data.body?.text || data.body.text.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['body', 'text'],
          message: 'Body text is required',
        });
      }
    }
  });

// Duplicate template schema
export const DuplicateTemplateSchema = z.object({
  projectId: z.string().min(1, 'Project ID is required'),
  newName: z
    .string()
    .min(1, 'New template name is required')
    .max(
      VALIDATION_LIMITS.MAX_TEMPLATE_NAME,
      `Template name must be less than ${VALIDATION_LIMITS.MAX_TEMPLATE_NAME} characters`
    )
    .superRefine((name, ctx) => {
      const errors = validateTemplateName(name);
      if (errors.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: errors[0],
        });
      }
    }),
});

// Template filters schema
export const TemplateFiltersSchema = z.object({
  projectId: z.string().min(1, 'Project ID is required'),
  category: z.enum(TEMPLATE_CATEGORIES).optional(),
  status: z
    .enum(['PENDING', 'APPROVED', 'REJECTED', 'PAUSED', 'PENDING_DELETION'])
    .optional(),
  search: z.string().optional(),
  sortBy: z
    .enum([
      'name',
      'category',
      'status',
      'createdAt',
      'updatedAt',
      'lastEditedAt',
    ])
    .optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(100).optional(),
});

// Export constants and helper functions
export {
  BUTTON_TYPES,
  detectDynamicVariables,
  HEADER_FORMATS,
  SUPPORTED_LANGUAGES,
  TEMPLATE_CATEGORIES,
  validatePhoneNumber,
  validateTemplateName,
  validateUrl,
  VALIDATION_LIMITS,
};

// Export default schema
export default CreateTemplateSchema;
